import React, { useState, useEffect } from "react";
import {
  Card,
  Upload,
  Button,
  List,
  message,
  Popconfirm,
  Tag,
  Typography,
  Space,
  Empty,
  Spin,
  Divider,
  Row,
  Col,
} from "antd";
import {
  UploadOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FileOutlined,
  PaperClipOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import {
  ContainerDocumentType,
  uploadContainerDocument,
  downloadContainerDocument,
  getContainerDocumentPreviewUrl,
  getFileType,
} from "../../utils/containerDocumentsUtils";
import {
  createContainerDocument,
  getContainerDocuments,
  deleteContainerDocument,
  ContainerDocument as ContainerDocumentInterface,
} from "../../services/containerDocumentsService";
import DocumentPreviewModal from "../BookingsAdmin/DocumentPreviewModal";
import dayjs from "dayjs";

const { Title, Text } = Typography;

interface ContainerDocumentsProps {
  containerId: number;
}

/**
 * Format file size for display
 */
const formatFileSize = (bytes: number | null): string => {
  if (!bytes) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

/**
 * Get document type display name
 */
const getDocumentTypeName = (type: ContainerDocumentType): string => {
  const names: Record<ContainerDocumentType, string> = {
    [ContainerDocumentType.AWB_BL]: "AWB/BL",
    [ContainerDocumentType.VRIJSTELLING]: "Vrijstelling",
    [ContainerDocumentType.T1_DOCUMENT]: "T1 Document",
  };
  return names[type] || type;
};

/** AWB/BL lives on Master BL/AWB — not uploaded per container. */
const CONTAINER_UPLOAD_DOCUMENT_TYPES = [
  ContainerDocumentType.VRIJSTELLING,
  ContainerDocumentType.T1_DOCUMENT,
] as const;

const ContainerDocuments: React.FC<ContainerDocumentsProps> = ({
  containerId,
}) => {
  const [documents, setDocuments] = useState<
    Record<ContainerDocumentType, ContainerDocumentInterface[]>
  >({
    [ContainerDocumentType.AWB_BL]: [],
    [ContainerDocumentType.VRIJSTELLING]: [],
    [ContainerDocumentType.T1_DOCUMENT]: [],
  });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState<
    Record<ContainerDocumentType, boolean>
  >({
    [ContainerDocumentType.AWB_BL]: false,
    [ContainerDocumentType.VRIJSTELLING]: false,
    [ContainerDocumentType.T1_DOCUMENT]: false,
  });
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewDocument, setPreviewDocument] =
    useState<ContainerDocumentInterface | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Fetch all documents for this container
  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const result = await getContainerDocuments(containerId);

      if (result.error || !result.success) {
        message.error("Failed to load container documents");
        return;
      }

      // Group documents by type
      const grouped: Record<
        ContainerDocumentType,
        ContainerDocumentInterface[]
      > = {
        [ContainerDocumentType.AWB_BL]: [],
        [ContainerDocumentType.VRIJSTELLING]: [],
        [ContainerDocumentType.T1_DOCUMENT]: [],
      };

      result.data.forEach((doc) => {
        grouped[doc.document_type].push(doc);
      });

      setDocuments(grouped);
    } catch (error) {
      console.error("Error fetching container documents:", error);
      message.error("Failed to load container documents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (containerId) {
      fetchDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerId]);

  // Handle file upload
  const handleUpload = async (
    documentType: ContainerDocumentType,
    file: File
  ) => {
    try {
      setUploading((prev) => ({ ...prev, [documentType]: true }));

      // Upload file to storage
      const uploadResult = await uploadContainerDocument(
        containerId,
        documentType,
        file
      );

      if (!uploadResult || uploadResult.error) {
        const errorMessage =
          uploadResult?.error &&
          typeof uploadResult.error === "object" &&
          "message" in uploadResult.error
            ? uploadResult.error.message
            : "Unknown error";

        // Check if it's a bucket not found error
        if (
          errorMessage.includes("Bucket not found") ||
          errorMessage.includes("404")
        ) {
          message.error({
            content: (
              <div>
                <strong>Storage bucket not found</strong>
                <br />
                <span style={{ fontSize: "12px" }}>
                  Please create the "container_documents" bucket in Supabase Storage.
                  <br />
                  Go to Storage → Create Bucket → Name: "container_documents"
                </span>
              </div>
            ),
            duration: 10,
          });
        } else {
          message.error(
            `Failed to upload ${getDocumentTypeName(documentType)}: ${errorMessage}`
          );
        }
        return;
      }

      // Create database record
      const createResult = await createContainerDocument(
        containerId,
        documentType,
        uploadResult.path || "",
        file.name,
        file.size
      );

      if (!createResult.success || createResult.error) {
        message.error(
          `Failed to save ${getDocumentTypeName(documentType)} record`
        );
        return;
      }

      message.success(
        `${getDocumentTypeName(documentType)} uploaded successfully`
      );
      await fetchDocuments();
      
      // Notify summary component to refresh
      const refreshFn = (window as any)[`container_${containerId}_refresh_docs`];
      if (refreshFn) refreshFn();
    } catch (error) {
      console.error("Error uploading document:", error);
      message.error("An error occurred while uploading the document");
    } finally {
      setUploading((prev) => ({ ...prev, [documentType]: false }));
    }
  };

  // Handle file preview
  const handlePreview = async (document: ContainerDocumentInterface) => {
    try {
      const fileInfo = getFileType(document.file_name);

      if (fileInfo.type === "other") {
        message.info(
          "Preview not available for this file type. Downloading instead..."
        );
        handleDownload(document);
        return;
      }

      setPreviewDocument(document);
      setPreviewVisible(true);

      const url = await getContainerDocumentPreviewUrl(document.file_path);
      if (!url) {
        message.error("Failed to load preview");
        setPreviewVisible(false);
        return;
      }

      setPreviewUrl(url);
    } catch (error) {
      console.error("Error previewing document:", error);
      message.error("Failed to preview document");
      setPreviewVisible(false);
    }
  };

  // Handle file download
  const handleDownload = async (document: ContainerDocumentInterface) => {
    try {
      await downloadContainerDocument(document.file_path, document.file_name);
      // Only show success for non-PDF downloads (PDFs open in new tab)
      const fileInfo = getFileType(document.file_name);
      if (fileInfo.type !== "pdf") {
        message.success("Document downloaded successfully");
      }
    } catch (error) {
      console.error("Error downloading document:", error);
      message.error("Failed to download document");
    }
  };

  // Handle file delete
  const handleDelete = async (documentId: number) => {
    try {
      const result = await deleteContainerDocument(documentId);

      if (!result.success || result.error) {
        message.error("Failed to delete document");
        return;
      }

      message.success("Document deleted successfully");
      await fetchDocuments();
      
      // Notify summary component to refresh
      const refreshFn = (window as any)[`container_${containerId}_refresh_docs`];
      if (refreshFn) refreshFn();
    } catch (error) {
      console.error("Error deleting document:", error);
      message.error("An error occurred while deleting the document");
    }
  };

  // Render upload component for each document type
  const renderDocumentSection = (type: ContainerDocumentType) => {
    const typeDocuments = documents[type];
    const isUploading = uploading[type];

    const uploadProps = {
      beforeUpload: (file: File) => {
        handleUpload(type, file);
        return false; // Prevent default upload
      },
      showUploadList: false,
      multiple: true,
      accept: "*", // Accept all file types
    };

    return (
      <Card
        key={type}
        title={
          <Space size="small">
            <PaperClipOutlined />
            <span style={{ fontSize: 14 }}>{getDocumentTypeName(type)}</span>
            {typeDocuments.length > 0 && (
              <Tag color="blue" style={{ margin: 0 }}>
                {typeDocuments.length}
              </Tag>
            )}
          </Space>
        }
        style={{ height: "100%" }}
        bodyStyle={{ padding: "16px" }}
        size="small"
      >
        <Space direction="vertical" style={{ width: "100%" }} size="small">
          <Upload {...uploadProps}>
            <Button
              icon={<UploadOutlined />}
              loading={isUploading}
              disabled={isUploading}
              block
              size="small"
            >
              {isUploading ? "Uploading..." : "Upload"}
            </Button>
          </Upload>

          {typeDocuments.length > 0 ? (
            <List
              dataSource={typeDocuments}
              size="small"
              style={{ marginTop: 8 }}
              renderItem={(document) => (
                <List.Item
                  style={{
                    padding: "8px 0",
                    borderBottom: "1px solid #f0f0f0",
                    cursor:
                      getFileType(document.file_name).type !== "other"
                        ? "pointer"
                        : "default",
                  }}
                  onClick={() => {
                    const fileInfo = getFileType(document.file_name);
                    if (fileInfo.type !== "other") {
                      handlePreview(document);
                    }
                  }}
                  actions={[
                    <Button
                      key="preview"
                      type="text"
                      icon={<EyeOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePreview(document);
                      }}
                      size="small"
                      disabled={
                        getFileType(document.file_name).type === "other"
                      }
                      style={{ padding: "0 4px" }}
                    />,
                    <Button
                      key="download"
                      type="text"
                      icon={<DownloadOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(document);
                      }}
                      size="small"
                      style={{ padding: "0 4px" }}
                    />,
                    <Popconfirm
                      key="delete"
                      title="Delete this document?"
                      description="This action cannot be undone."
                      onConfirm={(e) => {
                        if (e) e.stopPropagation();
                        handleDelete(document.id);
                      }}
                      onCancel={(e) => {
                        if (e) e.stopPropagation();
                      }}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        size="small"
                        style={{ padding: "0 4px" }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Popconfirm>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <FileOutlined
                        style={{ fontSize: 16, color: "#1890ff" }}
                      />
                    }
                    title={
                      <Text
                        strong
                        ellipsis
                        style={{
                          fontSize: 12,
                          maxWidth: "200px",
                          display: "block",
                        }}
                        title={document.file_name}
                      >
                        {document.file_name}
                      </Text>
                    }
                    description={
                      <Space size="small" style={{ fontSize: 11 }}>
                        <Text type="secondary">
                          {formatFileSize(document.file_size)}
                        </Text>
                        <Text type="secondary">•</Text>
                        <Text type="secondary">
                          {dayjs(document.created_at).format("DD/MM/YY HH:mm")}
                        </Text>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          ) : (
            <Empty
              description={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  No documents
                </Text>
              }
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              style={{ padding: "16px 0", marginTop: 8 }}
            />
          )}
        </Space>
      </Card>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "40px" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Card
      id="container-documents-section"
      title={
        <Space>
          <PaperClipOutlined />
          <span>Container Documents</span>
        </Space>
      }
      style={{ marginTop: 24 }}
    >
      <Text type="secondary" style={{ marginBottom: 16, display: "block" }}>
        Upload Vrijstelling and T1 documents for this container. AWB/BL is
        managed on the linked Master BL/AWB (admin/agent only).
      </Text>
      <Divider />
      <Row gutter={[16, 16]}>
        {CONTAINER_UPLOAD_DOCUMENT_TYPES.map((type) => (
          <Col key={type} xs={24} sm={24} md={8} lg={8} xl={8}>
            {renderDocumentSection(type)}
          </Col>
        ))}
      </Row>
      
      {/* Preview Modal */}
      <DocumentPreviewModal
        visible={previewVisible}
        onClose={() => {
          if (previewUrl) URL.revokeObjectURL(previewUrl);
          setPreviewVisible(false);
          setPreviewUrl(null);
          setPreviewDocument(null);
        }}
        fileName={previewDocument?.file_name || ""}
        blobUrl={previewUrl}
        onDownload={
          previewDocument
            ? () => handleDownload(previewDocument)
            : undefined
        }
      />
    </Card>
  );
};

export default ContainerDocuments;

